import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Medal, Award, TrendingUp } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaders(response.data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-8 h-8 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-8 h-8 text-slate-400" />;
    if (rank === 3) return <Medal className="w-8 h-8 text-orange-600" />;
    return <Award className="w-6 h-6 text-slate-400" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading leaderboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 p-8">
          <div className="flex items-center space-x-3">
            <Trophy className="w-10 h-10 text-yellow-600" />
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Leaderboard</h1>
              <p className="text-slate-600">Top students preparing for SSC 10th exams</p>
            </div>
          </div>
        </div>

        {/* Top 3 */}
        {leaders.length >= 3 && (
          <div className="grid md:grid-cols-3 gap-6">
            {leaders.slice(0, 3).map((leader, idx) => (
              <div
                key={leader.user_id}
                className={`bg-white rounded-lg border-2 p-6 text-center ${
                  idx === 0 ? 'border-yellow-400 shadow-lg' :
                  idx === 1 ? 'border-slate-300' :
                  'border-orange-400'
                }`}
                data-testid={`top-${idx + 1}-user`}
              >
                <div className="flex justify-center mb-4">
                  {getRankIcon(idx + 1)}
                </div>
                {leader.picture && (
                  <img
                    src={leader.picture}
                    alt={leader.name}
                    className="w-20 h-20 rounded-full mx-auto mb-3"
                  />
                )}
                <h3 className="text-xl font-bold text-slate-900">{leader.name}</h3>
                <p className="text-3xl font-bold text-blue-900 mt-2">{leader.points}</p>
                <p className="text-sm text-slate-600">points</p>
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-sm">
                  <p>📝 {leader.total_questions} questions</p>
                  <p>📊 {leader.total_tests} tests</p>
                  <p>🔥 {leader.streak_days} day streak</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rest of leaderboard */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">All Rankings</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {leaders.slice(3).map((leader) => (
              <div
                key={leader.user_id}
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`rank-${leader.rank}-user`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 text-center">
                      <span className="text-2xl font-bold text-slate-400">#{leader.rank}</span>
                    </div>
                    {leader.picture && (
                      <img
                        src={leader.picture}
                        alt={leader.name}
                        className="w-12 h-12 rounded-full"
                      />
                    )}
                    <div>
                      <h4 className="font-bold text-slate-900">{leader.name}</h4>
                      <p className="text-sm text-slate-600">
                        {leader.total_questions} questions • {leader.total_tests} tests
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-900">{leader.points}</p>
                    <p className="text-xs text-slate-500">points</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info card */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            How to Earn Points
          </h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li>• Ask a question: <strong>10 points</strong></li>
            <li>• Complete a practice test: <strong>20 points</strong></li>
            <li>• Daily login streak: <strong>5 points/day</strong></li>
            <li>• Bookmark helpful answers: <strong>2 points</strong></li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
